import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './AccountInfoModal.scss';
import { ProfileEditModal } from './ProfileEditModal';
import { validateAccountInfo } from './validation';
// TODO: Replace with API endpoint for professions when available
const professions = ['Architect', 'Interior Designer'];

interface AccountInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (data: Record<string, any>) => Promise<void>;
  initialData: {
    firstName: string;
    lastName: string;
    accountInformations?: {
      professionalEmail: string;
      personalEmail?: string;
      websitePortfolio?: string;
      instagramUrl?: string;
      linkedinUrl?: string;
      profession: string;
    };
  };
}

export const AccountInfoModal: React.FC<AccountInfoModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialData,
}) => {
  const { t } = useTranslation('profile');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (data: Record<string, any>) => {
    try {
      setIsSubmitting(true);
      // TODO: Implement API call to update account information
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      await onSuccess(data);
      onClose();
    } catch (error) {
      console.error('Error updating account information:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ProfileEditModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={t('sections.account.title').toUpperCase()}
      validate={validateAccountInfo}
      isSubmitting={isSubmitting}
    >
      <div className="form-groups-grid-account">
        <div className="form-group">
          <label htmlFor="firstName">{t('sections.account.firstName')}*</label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            defaultValue={initialData.firstName}
            disabled
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="lastName">{t('sections.account.lastName')}*</label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            defaultValue={initialData.lastName}
            disabled
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="professionalEmail">{t('sections.account.professionalEmail')}</label>
          <input
            type="email"
            id="professionalEmail"
            name="professionalEmail"
            defaultValue={initialData.accountInformations?.professionalEmail || ''}
          />
        </div>

        <div className="form-group">
          <label htmlFor="personalEmail">{t('sections.account.personalEmail')}</label>
          <input
            type="email"
            id="personalEmail"
            name="personalEmail"
            defaultValue={initialData.accountInformations?.personalEmail || ''}
          />
        </div>

        <div className="form-group">
          <div className="label-wrapper">
            <label htmlFor="websitePortfolio">{t('sections.account.websitePortfolio')}</label>
            <div className="helper-text">{t('sections.account.websiteHelperText')}</div>
          </div>
          <input
            type="url"
            id="websitePortfolio"
            name="websitePortfolio"
            defaultValue={initialData.accountInformations?.websitePortfolio || ''}
            placeholder=""
          />
        </div>

        <div className="form-group">
          <label htmlFor="instagramUrl">{t('sections.account.instagramProfile')}</label>
          <input
            type="text"
            id="instagramUrl"
            name="instagramUrl"
            defaultValue={initialData.accountInformations?.instagramUrl || ''}
          />
        </div>

        <div className="form-group">
          <label htmlFor="linkedinUrl">{t('sections.account.linkedinProfile')}</label>
          <input
            type="text"
            id="linkedinUrl"
            name="linkedinUrl"
            defaultValue={initialData.accountInformations?.linkedinUrl || ''}
          />
        </div>

        <div className="form-group">
          <label htmlFor="profession">{t('sections.account.profession')}</label>
          <div className="select-wrapper">
            <select
              id="profession"
              name="profession"
              defaultValue={initialData.accountInformations?.profession || ''}
            >
              <option value="">{t('sections.account.selectProfession')}</option>
              {professions.map((profession) => (
                <option key={profession} value={profession}>
                  {profession}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </ProfileEditModal>
  );
};
